// frontend/src/pages/admin/ManageDrawsPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Draw } from '../../types'; // Removed UserInfo as it's not directly used here
import { toast } from 'react-toastify';

interface WinnerDetail {
    userId: string;
    fullName: string;
    email: string;
    phoneNumber?: string;
    selectedNumbers: number[];
    payoutAmount: number;
    bankName: string; // Add bankName
    bankBranch: string; // Add bankBranch
    accountNumber: string; // Add accountNumber
}

const ManageDrawsPage: React.FC = () => {
    const { user } = useAuth();
    const [allDrawsData, setAllDrawsData] = useState<Draw[]>([]); // Stores all fetched draws
    const [draws, setDraws] = useState<Draw[]>([]); // Stores filtered/displayed draws
    const [loading, setLoading] = useState(true);
    const [showDeclareWinnerModal, setShowDeclareWinnerModal] = useState(false);
    const [selectedDrawForWinner, setSelectedDrawForWinner] = useState<Draw | null>(null);
    const [winningNumber, setWinningNumber] = useState<number | ''>('');

    // State for Edit Draw modal
    const [showEditDrawModal, setShowEditDrawModal] = useState(false);
    const [editedDraw, setEditedDraw] = useState<Draw | null>(null);

    // NEW STATES for Winner Details Modal
    const [showWinnerDetailsModal, setShowWinnerDetailsModal] = useState(false);
    const [currentWinningDrawName, setCurrentWinningDrawName] = useState('');
    const [currentWinningNumber, setCurrentWinningNumber] = useState<number | null>(null);
    const [winnerDetails, setWinnerDetails] = useState<WinnerDetail[]>([]);
    const [fetchingWinnerDetails, setFetchingWinnerDetails] = useState(false);

    // NEW STATES for filtering and searching
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('All'); // 'All', 'active', 'closed', 'declared'


    // Custom Tailwind animations via CSS (copied for consistency)
    const customAnimations = `
        @keyframes fade-in-down {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoom-in-out {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
        }
        @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        .animate-fade-in-down {
            animation: fade-in-down 0.8s ease-out forwards;
        }
        .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out forwards;
        }
        .animate-zoom-in-out {
            animation: zoom-in-out 0.5s ease-out forwards;
        }
        .animate-bounce-subtle {
            animation: bounce-subtle 1.5s infinite ease-in-out;
        }
        .animate-pulse-slow {
            animation: pulse-slow 3s infinite ease-in-out;
        }
    `;

    const fetchDraws = useCallback(async () => {
        setLoading(true);
        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to view this page.');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/draws`, {
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                },
            });
            const data = await response.json();
            if (response.ok) {
                setAllDrawsData(data); // Store original data
            } else {
                throw new Error(data.message || 'Failed to fetch draws.');
            }
        } catch (err: any) {
            toast.error(err.message || 'Error fetching draws.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchDraws();
    }, [fetchDraws]);

    // Apply filters whenever allDrawsData, searchTerm, or filterStatus changes
    useEffect(() => {
        let filteredDraws = allDrawsData;

        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            filteredDraws = filteredDraws.filter(draw =>
                draw.name.toLowerCase().includes(lowerCaseSearchTerm)
            );
        }

        if (filterStatus !== 'All') {
            filteredDraws = filteredDraws.filter(draw => draw.status === filterStatus);
        }

        setDraws(filteredDraws); // Set the filtered draws to be displayed
    }, [allDrawsData, searchTerm, filterStatus]);


    const handleUpdateStatus = useCallback(async (drawId: string, status: 'active' | 'closed' | 'declared') => {
        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to perform this action.');
            return;
        }

        // IMPORTANT: Replace window.confirm with a custom modal for better UX and consistency
        if (!window.confirm(`Are you sure you want to change the status to "${status}"?`)) {
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/draws/${drawId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({ status }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || `Failed to update draw status to ${status}.`);
            }

            toast.success(data.message || `Draw status updated to ${status} successfully!`);
            fetchDraws(); // Re-fetch draws to update the list
        } catch (err: any) {
            toast.error(err.message || 'Error updating draw status.');
        }
    }, [user, fetchDraws]);

    const handleDeleteDraw = useCallback(async (drawId: string) => {
        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to perform this action.');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this draw? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/draws/${drawId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete draw.');
            }

            toast.success(data.message || 'Draw deleted successfully!');
            fetchDraws(); // Re-fetch draws to update the list
        } catch (err: any) {
            toast.error(err.message || 'Error deleting draw.');
        }
    }, [user, fetchDraws]);

    const openDeclareWinnerModal = (draw: Draw) => {
        setSelectedDrawForWinner(draw);
        // Pre-fill winning number if already declared
        setWinningNumber(typeof draw.winningNumber === 'number' ? draw.winningNumber : '');
        setShowDeclareWinnerModal(true);
    };

    const handleDeclareWinner = useCallback(async () => {
        if (!selectedDrawForWinner || winningNumber === '') {
            toast.error('Please select a draw and enter a winning number.');
            return;
        }

        const parsedWinningNumber = Number(winningNumber);
        if (isNaN(parsedWinningNumber) || parsedWinningNumber < 0 || parsedWinningNumber > 99) {
            toast.error('Winning number must be between 0 and 99.');
            return;
        }

        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to declare a winner.');
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/draws/${selectedDrawForWinner._id}/declare-winner`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({ winningNumber: parsedWinningNumber }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to declare winner.');
            }

            toast.success(data.message || `Winner declared for draw ${selectedDrawForWinner.name}!`);
            setShowDeclareWinnerModal(false);
            fetchDraws(); // Re-fetch draws to update the list and show declared status
        } catch (err: any) {
            toast.error(err.message || 'Error declaring winner.');
        }
    }, [user, selectedDrawForWinner, winningNumber, fetchDraws]);

    // Functions for Edit Draw functionality
    const openEditDrawModal = (draw: Draw) => {
        setEditedDraw({ ...draw }); // Create a copy to edit
        setShowEditDrawModal(true);
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setEditedDraw(prev => {
            if (!prev) return null;
            if (name === 'entryCost' || name === 'prizeValue') {
                return { ...prev, [name]: parseFloat(value) || 0 };
            }
            if (name === 'deadline') {
                 return { ...prev, [name]: value };
            }
            return { ...prev, [name]: value };
        });
    };

    const handleUpdateDraw = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editedDraw) return;

        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to update draw.');
            return;
        }

        // Basic validation for edited fields
        if (!editedDraw.name || editedDraw.name.trim() === '') {
            toast.error('Draw name cannot be empty.');
            return;
        }
        if (editedDraw.entryCost <= 0) {
            toast.error('Entry cost must be greater than zero.');
            return;
        }
        if (editedDraw.prizeValue <= 0) {
            toast.error('Prize value must be greater than zero.');
            return;
        }
        if (editedDraw.prizePoolType === 'percentage' && (editedDraw.prizeValue < 1 || editedDraw.prizeValue > 100)) {
            toast.error('Percentage prize value must be between 1 and 100.');
            return;
        }
        // Validate deadline only if the status is active or if changing to active
        // NOTE: The backend will also validate this. This is a client-side preliminary check.
        if (editedDraw.status === 'active' && new Date(editedDraw.deadline) <= new Date()) {
            toast.error('Deadline for an active draw must be in the future.');
            return;
        }


        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/draws/${editedDraw._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({
                    name: editedDraw.name,
                    // Send deadline as an ISO string for backend parsing
                    deadline: new Date(editedDraw.deadline).toISOString(),
                    entryCost: editedDraw.entryCost,
                    prizePoolType: editedDraw.prizePoolType,
                    prizeValue: editedDraw.prizeValue,
                    // Do NOT send status or winningNumber here, use dedicated handlers for those
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to update draw.');
            }

            toast.success(`Draw "${editedDraw.name}" updated successfully!`);
            setShowEditDrawModal(false);
            fetchDraws();
        } catch (err: any) {
            toast.error(err.message || 'Error updating draw.');
        }
    }, [user, editedDraw, fetchDraws]);

    // NEW: Handle viewing winner details
    const handleViewWinners = useCallback(async (draw: Draw) => {
        setFetchingWinnerDetails(true);
        setWinnerDetails([]); // Clear previous details
        setCurrentWinningDrawName(draw.name);
        setCurrentWinningNumber(draw.winningNumber || null); // Ensure it's a number or null

        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to view winner details.');
            setFetchingWinnerDetails(false);
            return;
        }
        if (draw.status !== 'declared' || typeof draw.winningNumber !== 'number') {
            toast.error('Winner has not been declared for this draw yet.');
            setFetchingWinnerDetails(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/draws/${draw._id}/winner-details`, {
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                },
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to fetch winner details.');
            }

            if (data.winners && data.winners.length > 0) {
                setWinnerDetails(data.winners);
            } else {
                toast.info('No winning bets found for this draw.');
            }
            setShowWinnerDetailsModal(true); // Open the winner details modal
        } catch (err: any) {
            toast.error(err.message || 'Error fetching winner details.');
        } finally {
            setFetchingWinnerDetails(false);
        }
    }, [user]);


    if (loading && !allDrawsData.length) { // Check allDrawsData for initial load
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-200 font-inter">
                <style>{customAnimations}</style>
                <div className="text-center">
                    <p className="text-2xl font-semibold text-purple-700 animate-pulse-slow">
                        Loading draws...
                    </p>
                    <div className="mt-4 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
            </div>
        );
    }

    if (!user || !user.isAdmin) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100 font-inter">
                <style>{customAnimations}</style>
                <div className="bg-white p-8 rounded-lg shadow-xl border-t-8 border-red-500 w-full max-w-md text-center animate-zoom-in-out">
                    <h2 className="text-3xl font-bold text-red-700 mb-4">Access Denied!</h2>
                    <p className="text-lg text-gray-700">You do not have administrative privileges to view this page.</p>
                </div>
            </div>
        );
    }

    // Helper to format ISO date string for datetime-local input
    const formatForDatetimeLocal = (isoString: string) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        // Date.toISOString() returns 'YYYY-MM-DDTHH:mm:ss.sssZ'.
        // We need 'YYYY-MM-DDTHH:mm' for datetime-local input.
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return (
        <div className="container mx-auto p-4 md:p-8 min-h-screen font-inter bg-gradient-to-br from-blue-100 to-purple-200">
            <style>{customAnimations}</style> {/* Inject custom animations */}

            <h1 className="text-4xl font-extrabold text-center mb-10 text-indigo-800 animate-fade-in-down">
                🎲 Manage Lucky Draws 🎲
            </h1>

            {/* Filter and Search Section */}
            <div className="bg-white p-6 rounded-2xl shadow-3xl mb-8 border-t-8 border-purple-400 animate-zoom-in-out">
                <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">Filter & Search Draws</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label htmlFor="searchTerm" className="block text-gray-700 text-lg font-bold mb-2">Search Draw Name:</label>
                        <input
                            type="text"
                            id="searchTerm"
                            className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out text-lg shadow-sm"
                            placeholder="Draw Name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div>
                        <label htmlFor="filterStatus" className="block text-gray-700 text-lg font-bold mb-2">Filter by Status:</label>
                        <select
                            id="filterStatus"
                            className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out text-lg shadow-sm bg-white"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="All">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="closed">Closed</option>
                            <option value="declared">Declared</option>
                        </select>
                    </div>
                </div>
                <p className="text-sm text-gray-500 text-center">Filters apply automatically as you type or select.</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-3xl border-t-8 border-purple-500 animate-zoom-in-out">
                <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">All Draws ({draws.length})</h2>
                {draws.length === 0 ? (
                    <p className="text-center text-gray-600 text-lg py-8">No draws found matching the current filters.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {draws.map(draw => (
                            <div key={draw._id} className="relative bg-gray-50 p-6 rounded-xl shadow-lg border border-gray-200 transform transition-transform duration-300 hover:scale-[1.02] hover:shadow-2xl animate-fade-in-up">
                                {/* Status Badge */}
                                <div className={`absolute top-0 right-0 m-3 px-3 py-1 rounded-full text-white text-xs font-bold uppercase shadow-md
                                    ${draw.status === 'active' ? 'bg-green-600' :
                                      draw.status === 'closed' ? 'bg-red-600' :
                                      draw.status === 'declared' ? 'bg-blue-600' : 'bg-gray-600'
                                    }`}>
                                    {draw.status}
                                </div>

                                <h3 className="text-2xl font-bold text-indigo-700 mb-2 mt-2">{draw.name}</h3>
                                <p className="text-sm text-gray-600 mb-1">
                                    <span className="font-semibold">Deadline:</span> {new Date(draw.deadline).toLocaleString()}
                                </p>
                                <p className="text-md text-gray-700 mb-1">
                                    <span className="font-semibold">Entry Cost:</span> <span className="font-bold text-green-700">Rs. {draw.entryCost.toFixed(2)}</span>
                                </p>
                                <p className="text-md text-gray-700 mb-3">
                                    <span className="font-semibold">Prize Pool:</span> <span className="font-bold text-purple-700">{draw.prizeValue}{draw.prizePoolType === 'percentage' ? '%' : ' Rs.'}</span>
                                </p>

                                {/* Winning Number Display */}
                                {draw.status === 'declared' && typeof draw.winningNumber === 'number' && (
                                    <div className="bg-blue-100 border border-blue-300 rounded-md p-2 text-center mb-3">
                                        <p className="text-md text-blue-800 font-bold">Winning Number:</p>
                                        <p className="text-3xl font-extrabold text-blue-900 leading-none">
                                            {draw.winningNumber < 10 ? `0${draw.winningNumber}` : draw.winningNumber}
                                        </p>
                                    </div>
                                )}
                                {draw.lockedNumbers && Array.isArray(draw.lockedNumbers) && draw.lockedNumbers.length > 0 && draw.status === 'active' && (
                                    <p className="text-sm text-purple-700 font-semibold mt-1">
                                        🔥 {draw.lockedNumbers.length} Numbers Picked!
                                    </p>
                                )}

                                <div className="mt-4 flex flex-wrap gap-2 justify-end">
                                    {/* Edit Button */}
                                    <button
                                        onClick={() => openEditDrawModal(draw)}
                                        className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-md text-sm transition duration-300 shadow-md transform hover:scale-105"
                                    >
                                        Edit
                                    </button>
                                    {draw.status === 'active' && (
                                        <button
                                            onClick={() => handleUpdateStatus(draw._id, 'closed')}
                                            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-md text-sm transition duration-300 shadow-md transform hover:scale-105"
                                        >
                                            Close Draw
                                        </button>
                                    )}
                                    {/* Modified button for Declare/Re-declare */}
                                    {(draw.status === 'closed' || draw.status === 'declared') && (
                                        <button
                                            onClick={() => openDeclareWinnerModal(draw)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md text-sm transition duration-300 shadow-md transform hover:scale-105"
                                        >
                                            {draw.status === 'declared' ? 'Re-declare Winner' : 'Declare Winner'}
                                        </button>
                                    )}
                                    {/* NEW: View Winners button for declared draws */}
                                    {draw.status === 'declared' && typeof draw.winningNumber === 'number' && (
                                        <button
                                            onClick={() => handleViewWinners(draw)}
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-md text-sm transition duration-300 shadow-md transform hover:scale-105"
                                            disabled={fetchingWinnerDetails}
                                        >
                                            {fetchingWinnerDetails ? 'Fetching...' : 'View Winners'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeleteDraw(draw._id)}
                                        className="bg-gray-400 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-md text-sm transition duration-300 shadow-md transform hover:scale-105"
                                    >
                                        Delete Draw
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Declare Winner Modal */}
            {showDeclareWinnerModal && selectedDrawForWinner && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md relative transform scale-100 animate-zoom-in-out border-t-8 border-blue-600">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center text-blue-700">
                            {selectedDrawForWinner.status === 'declared' ? 'Re-declare Winner for:' : 'Declare Winner for:'} <span className="font-extrabold">{selectedDrawForWinner.name}</span>
                        </h2>
                        <button
                            onClick={() => setShowDeclareWinnerModal(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-3xl font-light"
                            title="Close"
                        >
                            &times;
                        </button>

                        <div className="mb-8">
                            <label htmlFor="winningNumber" className="block text-gray-700 text-lg font-semibold mb-2">
                                Enter Winning Number (0-99)
                            </label>
                            <input
                                type="number"
                                id="winningNumber"
                                value={winningNumber} // Pre-fills if draw is declared
                                onChange={(e) => setWinningNumber(parseInt(e.target.value) || '')}
                                placeholder="e.g., 55"
                                min="0"
                                max="99"
                                className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out text-lg shadow-sm"
                                required
                            />
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeclareWinnerModal(false)}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-5 rounded-md transition duration-300 shadow-md"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeclareWinner}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-5 rounded-md transition duration-300 shadow-md"
                                disabled={winningNumber === ''} // Disable if winning number is empty
                            >
                                {selectedDrawForWinner.status === 'declared' ? 'Re-declare Winner' : 'Declare Winner'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Draw Modal */}
            {showEditDrawModal && editedDraw && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md relative transform scale-100 animate-zoom-in-out border-t-8 border-yellow-600">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center text-yellow-700">Edit Draw: <span className="font-extrabold">{editedDraw.name}</span></h2>
                        <button
                            onClick={() => setShowEditDrawModal(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-3xl font-light"
                            title="Close"
                        >
                            &times;
                        </button>

                        <form onSubmit={handleUpdateDraw} className="space-y-5">
                            <div>
                                <label htmlFor="name" className="block text-gray-700 text-lg font-semibold mb-2">Draw Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={editedDraw.name}
                                    onChange={handleEditChange}
                                    className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 transition duration-150 ease-in-out text-lg shadow-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="deadline" className="block text-gray-700 text-lg font-semibold mb-2">Deadline</label>
                                <input
                                    type="datetime-local"
                                    id="deadline"
                                    name="deadline"
                                    value={formatForDatetimeLocal(editedDraw.deadline)}
                                    onChange={handleEditChange}
                                    className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 transition duration-150 ease-in-out text-lg shadow-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="entryCost" className="block text-gray-700 text-lg font-semibold mb-2">Entry Cost (Rs.)</label>
                                <input
                                    type="number"
                                    id="entryCost"
                                    name="entryCost"
                                    value={editedDraw.entryCost}
                                    onChange={handleEditChange}
                                    className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 transition duration-150 ease-in-out text-lg shadow-sm"
                                    required
                                    min="0.01"
                                    step="0.01"
                                />
                            </div>
                            <div>
                                <label htmlFor="prizePoolType" className="block text-gray-700 text-lg font-semibold mb-2">Prize Pool Type</label>
                                <select
                                    id="prizePoolType"
                                    name="prizePoolType"
                                    value={editedDraw.prizePoolType}
                                    onChange={handleEditChange}
                                    className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 transition duration-150 ease-in-out text-lg shadow-sm bg-white"
                                    required
                                >
                                    <option value="fixed">Fixed</option>
                                    <option value="percentage">Percentage</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="prizeValue" className="block text-gray-700 text-lg font-semibold mb-2">Prize Value</label>
                                <input
                                    type="number"
                                    id="prizeValue"
                                    name="prizeValue"
                                    value={editedDraw.prizeValue}
                                    onChange={handleEditChange}
                                    className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 transition duration-150 ease-in-out text-lg shadow-sm"
                                    required
                                    min={editedDraw.prizePoolType === 'percentage' ? "1" : "0.01"}
                                    max={editedDraw.prizePoolType === 'percentage' ? "100" : undefined}
                                    step="0.01"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => setShowEditDrawModal(false)}
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-5 rounded-md transition duration-300 shadow-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-5 rounded-md transition duration-300 shadow-md"
                                >
                                    Update Draw
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* NEW: Winner Details Modal */}
            {showWinnerDetailsModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-xl relative transform scale-100 animate-zoom-in-out border-t-8 border-indigo-600">
                        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center text-indigo-700">
                            Winning Details for: <span className="font-extrabold">{currentWinningDrawName}</span>
                        </h2>
                        <button
                            onClick={() => setShowWinnerDetailsModal(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-3xl font-light"
                            title="Close"
                        >
                            &times;
                        </button>

                        <div className="text-center mb-6">
                            <p className="text-xl font-semibold text-gray-700">Winning Number:</p>
                            <p className="text-5xl font-extrabold text-green-600 leading-none">
                                {currentWinningNumber !== null ? (currentWinningNumber < 10 ? `0${currentWinningNumber}` : currentWinningNumber) : 'N/A'}
                            </p>
                        </div>

                        {fetchingWinnerDetails ? (
                            <p className="text-center text-blue-600 text-lg mt-4 animate-pulse">Loading winner details...</p>
                        ) : winnerDetails.length === 0 ? (
                            <p className="text-center text-gray-600 text-lg mt-4">No winning bets found for this draw.</p>
                        ) : (
                            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                                {winnerDetails.map((winner, index) => (
                                    <div key={index} className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-200">
                                        <p className="text-lg font-bold text-blue-800">Winner: {winner.fullName}</p>
                                        <p className="text-md text-gray-700">Email: {winner.email}</p>
                                        {winner.phoneNumber && <p className="text-md text-gray-700">Phone: {winner.phoneNumber}</p>}
                                        <p className="text-md text-gray-700">Bet Numbers: <span className="font-semibold">{winner.selectedNumbers.join(', ')}</span></p>
                                        <p className="text-md text-gray-700">Bank Name: <span className="font-semibold">{winner.bankName}</span></p>
                                        <p className="text-md text-gray-700">Bank Branch: <span className="font-semibold">{winner.bankBranch}</span></p>
                                        <p className="text-md text-gray-700">Account Number: <span className="font-semibold">{winner.accountNumber}</span></p>
                                        <p className="text-lg font-bold text-green-700">Payout: Rs. {winner.payoutAmount.toFixed(2)}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-8 text-center">
                            <button
                                onClick={() => setShowWinnerDetailsModal(false)}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-md transition duration-300 shadow-lg text-xl transform hover:scale-105"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageDrawsPage;